import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Navbar from "../components/site/Navbar";
import ContactRail from "../components/site/ContactRail";
import { Footer } from "../components/site/Sections5";
import PageHero from "../components/site/PageHero";
import DedicatedCTA from "../components/site/DedicatedCTA";
import SEO from "../components/site/SEO";
import { SERVICES } from "../constants/servicesData";

export default function ServicesOverview() {
  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="services-overview-page">
      <SEO
        title="Services Overview | Brand, Multimedia & Digital Marketing"
        description="Explore ADMALL INDIA's 5 core marketing disciplines: Brand Engineering, Multimedia Production, Digital Marketing, Performance & Lead Generation, and AI-Powered Marketing."
        canonical="https://admallindia.com/services"
      />
      <Navbar logo="/admall-logo.png" />
      <ContactRail />

      <main className="flex-grow">
        <PageHero
          category="Capabilities"
          categoryLink="/services"
          title="Connected Marketing Disciplines"
          tagline="We unite brand strategy, cinematic video production, digital distribution, and measurable performance marketing under one accountable partner."
          bgImage="https://images.unsplash.com/photo-1781127445273-0f1fdca3a9f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          ctaLabel="Discuss A Project"
        />

        {/* Philosophy Intro */}
        <section className="bg-white py-16 md:py-24 border-b border-navy/10">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="max-w-3xl">
              <div className="mb-4 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                <span>Integrated Advantage</span>
              </div>
              <h2 className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy sm:text-5xl">
                Marketing shouldn't be a fragmented collection of vendors.
              </h2>
              <p className="mt-6 font-body text-base leading-relaxed text-navy/70 sm:text-lg">
                When branding agencies, video production houses, and digital media buyers operate in silos, marketing budgets leak and messaging fractures. ADMALL INDIA operates as your single integrated creative and performance partner—aligning creative vision with bottom-line commercial outcomes.
              </p>
            </div>
          </div>
        </section>

        {/* Services Directory */}
        <section className="bg-[#F8F9FA] py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid gap-12 lg:gap-16">
              {SERVICES.map((s, index) => (
                <article
                  key={s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className="overflow-hidden rounded-3xl border border-navy/10 bg-white shadow-sm transition hover:shadow-xl grid lg:grid-cols-12 gap-0"
                >
                  <div className={`relative min-h-[300px] lg:min-h-full overflow-hidden ${index % 2 === 1 ? "lg:col-span-5 lg:order-2" : "lg:col-span-5"}`}>
                    <img
                      src={s.heroImage}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition duration-700 hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent lg:hidden" />
                  </div>

                  <div className={`p-8 sm:p-10 lg:p-12 flex flex-col justify-between ${index % 2 === 1 ? "lg:col-span-7 lg:order-1" : "lg:col-span-7"}`}>
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                        <span className="font-body text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                          Discipline
                        </span>
                      </div>
                      <h3 className="font-display text-3xl font-black uppercase tracking-tight text-navy sm:text-4xl">
                        {s.title}
                      </h3>
                      <p className="mt-2 font-display text-base font-bold uppercase tracking-wide text-[#FF5722]">
                        {s.tagline}
                      </p>
                      <p className="mt-4 font-body text-sm leading-relaxed text-navy/70 sm:text-base">
                        {s.summary}
                      </p>

                      <div className="mt-6 flex flex-wrap gap-2">
                        {s.bullets.slice(0, 6).map((b, bi) => (
                          <span
                            key={bi}
                            className="inline-flex items-center gap-1.5 rounded-full bg-navy/5 px-3 py-1 font-body text-xs font-medium text-navy/80"
                          >
                            <CheckCircle2 size={12} className="text-[#FF5722]" />
                            {b}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-navy/10 flex flex-wrap items-center justify-between gap-4">
                      <Link
                        to={`/services/${s.slug}`}
                        className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722] hover:text-[#E64A19] transition"
                      >
                        Explore {s.title} Details <ArrowRight size={16} />
                      </Link>
                      <a
                        href="/#contact"
                        className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2 font-display text-xs font-bold uppercase tracking-wider text-navy hover:bg-navy hover:text-white transition"
                      >
                        Enquire Now
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <DedicatedCTA
          title="Looking for an Integrated Approach?"
          subtitle="You do not need to diagnose which specific marketing service to purchase. Share your business targets, and we will structure the optimal roadmap."
        />
      </main>

      <Footer
        content={{
          logo_url: "/admall-logo.png",
          footer: {
            tagline: "THINK. CREATE. REACH. PERFORM. GROW.",
            disciplines: "Brand Strategy | Creative | Multimedia | Digital Marketing | Advertising | Lead Generation | AI Marketing",
          },
          settings: {
            phone: "+91 93221 06632",
            phone_raw: "+919322106632",
            email: "admallindiagroup@gmail.com",
            location: "Navi Mumbai, Maharashtra, India",
            website: "admallindia.com",
          },
        }}
      />
    </div>
  );
}
